import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSpark, type SparkVariant } from '@estream/sdk-browser/react';
import { SparkRenderer, SparkVariantSelector } from '../components/SparkRenderer';
import './Login.css';

// Edge proxy URL for Spark API
const EDGE_API_URL = import.meta.env.VITE_EDGE_API_URL || 'https://edge.estream.dev';

export default function Login() {
  const navigate = useNavigate();
  const [variant, setVariant] = useState<SparkVariant>('kaleidoscope');
  
  const { 
    state, 
    startChallenge, 
    timeRemaining,
    isAuthenticated,
    challenge,
  } = useSpark({
    variant,
    // Use edge-proxy for Spark API (has ESLite for persistence)
    latticeUrl: EDGE_API_URL,
    // Lattice-based auth with polling fallback
    enablePolling: true,
    pollingInterval: 2000,
    // Retry on failure
    retryCount: 3,
    retryDelay: 1000,
    onAuthenticated: (session) => {
      console.log('[Login] Authenticated:', session.walletId.slice(0, 16) + '...');
      navigate('/');
    },
    onError: (error) => {
      console.error('[Login] Error:', error);
    },
  });
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Build challenge JSON for SparkRenderer
  // Memoize to prevent SparkRenderer from reinitializing every render
  // The challenge_id is encoded in the Spark visual for mobile scanning
  const challengeJson = useMemo(() => {
    if (!challenge) return undefined;
    return JSON.stringify({
      id: challenge.challengeId,
      device_id: '0'.repeat(64), // Placeholder
      nonce: challenge.challengeId.slice(0, 32),
      created_at: challenge.expiresAt - 300000, // Fixed: derive from expiresAt, not Date.now()
      expires_at: challenge.expiresAt,
      spark_code: challenge.displayCode,
      // New: Include session info for mobile app
      session_id: challenge.sessionId,
      action: challenge.payload.action,
      service: challenge.payload.service,
    });
  }, [challenge?.challengeId]); // Only recompute when challenge ID changes
  
  return (
    <div className="login-page">
      <div className="login-card">
        {/* Logo */}
        <div className="login-header">
          <div className="login-logo">
            <span className="logo-icon">⬡</span>
            <span className="logo-text">eStream</span>
          </div>
          <h1 className="login-title">Mission Control</h1>
        </div>
        
        {/* Spark Authentication */}
        <div className="spark-container">
          {state.status === 'idle' && (
            <>
              <p className="login-subtitle">
                Authenticate with your eStream Wallet
              </p>
              
              {/* Variant selector before starting */}
              <div className="variant-picker">
                <label className="variant-label">Spark Style</label>
                <SparkVariantSelector value={variant} onChange={setVariant} />
              </div>
              
              <button 
                className="btn btn-primary btn-large"
                onClick={startChallenge}
              >
                Start Spark Login
              </button>
            </>
          )}
          
          {(state.status === 'pending' || state.status === 'verifying') && challengeJson && (
            <>
              {/* Spark Renderer - animated visual */}
              <SparkRenderer
                challengeJson={challengeJson}
                variant={variant}
                size={280}
                showCode={false}
                onError={(error) => console.error('[Login] Spark error:', error)}
              />
              
              <p className="spark-instructions">
                Scan with your eStream Wallet app to authenticate
              </p>
              
              {/* Challenge ID for debugging */}
              <p className="challenge-id" title={challenge?.challengeId}>
                {challenge?.displayCode}
              </p>
              
              {timeRemaining !== null && (
                <p className={`spark-timer ${timeRemaining < 30 ? 'expiring' : ''}`}>
                  Expires in {timeRemaining}s
                </p>
              )}
              
              <div className="spark-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={startChallenge}
                >
                  Refresh
                </button>
                <SparkVariantSelector value={variant} onChange={setVariant} />
              </div>
            </>
          )}
          
          {state.status === 'expired' && (
            <>
              <div className="status-icon expired">⏰</div>
              <p className="login-error">Challenge expired</p>
              <button 
                className="btn btn-primary btn-large"
                onClick={startChallenge}
              >
                Try Again
              </button>
            </>
          )}
          
          {state.status === 'error' && (
            <>
              <div className="status-icon error">⚠️</div>
              <p className="login-error">{state.error}</p>
              <button 
                className="btn btn-primary btn-large"
                onClick={startChallenge}
              >
                Try Again
              </button>
            </>
          )}
        </div>
        
        {/* Protocol info */}
        <div className="protocol-info">
          <details>
            <summary>How it works</summary>
            <ol>
              <li>Console creates a secure challenge on the eStream lattice</li>
              <li>The animated Spark encodes the challenge ID</li>
              <li>Your wallet app scans and verifies the challenge</li>
              <li>Your signed response is sent via the lattice</li>
              <li>Console verifies and creates your session</li>
            </ol>
          </details>
        </div>
        
        {/* Footer */}
        <div className="login-footer">
          <p>
            Don't have an eStream Wallet?{' '}
            <a href="https://estream.dev/wallet" target="_blank" rel="noopener">
              Get started
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
